import {
  type FooterPaymentLogo,
  type FooterSocialLink,
  FooterVariant1,
} from '@workspace/shared/components/FooterVariant1';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { company } from '~/company/Company';
import { footerConfigs } from '~/company/configs/footerConfig';
import Icon from '~/components/Icon/Icon';
import type { IconTypes } from '~/components/Icon/IconTypes';
import type { PaymentMethods } from '~/types/payments';

const SOCIAL_LINKS: { link?: string; icon: IconTypes; label: string }[] = [
  { link: company.config.links.instagram, icon: 'insta', label: 'Instagram' },
  { link: company.config.links.facebook, icon: 'facebook', label: 'Facebook' },
  { link: company.config.links.tiktok, icon: 'tiktok', label: 'TikTok' },
  { link: company.config.links.youtube, icon: 'youtube', label: 'YouTube' },
];

const PAYMENT_METHODS: Partial<
  Record<PaymentMethods, { icon: IconTypes; width?: number }>
> = {
  cash: { icon: 'pay-cash-mono', width: 60 },
  gpay: { icon: 'pay-gpay-mono' },
  applepay: { icon: 'pay-apple-mono' },
  visa: { icon: 'pay-visa-mono' },
  mc: { icon: 'pay-mc-mono' },
  jcb: { icon: 'pay-jcb-mono' },
  unionpay: { icon: 'pay-unionpay-mono' },
  amex: { icon: 'pay-amex-mono' },
  discover: { icon: 'pay-discover-mono' },
};

const FooterElectronium = () => {
  const { t } = useTranslation();

  const footerConfig = footerConfigs[company.name] || footerConfigs.default;

  if (!footerConfig) {
    return null;
  }

  const menuColumns = footerConfig.sections.map((section) => ({
    title: t(section.title),
    items: section.items.map((item) => ({
      dataLink: item.dataLink || item.link,
      to: item.link,
      label: t(item.title),
    })),
  }));

  const socialLinks: FooterSocialLink[] = SOCIAL_LINKS.filter(
    (social) => social.link,
  ).map((social) => ({
    href: social.link as string,
    label: social.label,
    icon: <Icon type={social.icon} size={20} />,
  }));

  const paymentLogos = (company.config.paymentMethods ?? []).reduce<
    FooterPaymentLogo[]
  >((acc, method) => {
    const paymentMethod = PAYMENT_METHODS[method];

    if (paymentMethod) {
      acc.push({
        alt: method,
        node: (
          <Icon
            type={paymentMethod.icon}
            size={40}
            height={40}
            width={paymentMethod.width}
          />
        ),
      });
    }

    return acc;
  }, []);

  const supportUrl = company.config.links.whatsapp;

  return (
    <FooterVariant1
      logo={{
        src: company.images.logo.dark,
        to: '/',
      }}
      menuColumns={menuColumns}
      paymentLogos={paymentLogos}
      socialLinks={socialLinks}
      copyrightText={t('electroniumCopyright')}
      supportButton={
        supportUrl
          ? {
              href: supportUrl,
              label: t('support'),
              icon: <Icon type="message_text_circle" />,
            }
          : undefined
      }
      linkComponent={Link}
      dataTheme={company.name}
    />
  );
};

export default FooterElectronium;
